import React, { useEffect, useRef, useState } from "react";
import { MoreVertical } from "react-feather";

import { LabeledInput, LabeledSelect } from "@/Components/Form";
import { GAS_OPTIONS } from "@/Enums/gas_options";
import { SCOPE_OPTIONS } from "@/Enums/scopes";
import { IconEnter } from "@/Icons";
import { ActionMenu } from "@/Components/ActionMenu";
import { FactorGasComment } from "@/Components/EmissionFactors/FactorGasComment";
import { Info } from "@/Components/Info";

export function FactorGasCO2e({
  children,
  className = "",
  index = 0,
  gas = {},
  unit = "",
  isDisabled = false,
  onRemove = () => {},
  onFormUpdate = () => {},
  errors = {},
}) {
  const [isEditable, setIsEditable] = useState(!isDisabled);
  const [commentVisible, setCommentVisible] = useState(!!gas?.comment);

  const AR5GasMultipliers = {
    factor: 1,
    co2_factor: 1,
    ch4_factor: 28,
    n2o_factor: 265,
  };

  function syncGasValues(e) {
    const { name, value } = e.target;

    if (value === "") {
      // User deleting the value, do not calculate in case the user is correcting an errorg
      return;
    }

    const factors = Object.keys(AR5GasMultipliers);
    const filledFactors = factors.filter((f) => gas[f]);

    const updatedGas = {
      key: null,
      value: null,
    };

    if (filledFactors.length === 3) {
      const missingFactor = factors.find((f) => !filledFactors.includes(f));

      let sum = 0;

      if (missingFactor !== "factor") {
        // get all other factors except the missing factor and factor
        const allOtherFactors = factors.filter(
          (f) => f !== missingFactor && f !== "factor",
        );
        sum = Number(gas.factor);
        allOtherFactors.forEach((f) => {
          sum -= Number(gas[f]) * AR5GasMultipliers[f];
        });
        sum /= AR5GasMultipliers[missingFactor];
      } else {
        sum =
          Number(gas.co2_factor) * AR5GasMultipliers.co2_factor +
          Number(gas.ch4_factor) * AR5GasMultipliers.ch4_factor +
          Number(gas.n2o_factor) * AR5GasMultipliers.n2o_factor;
      }

      updatedGas.key = missingFactor;
      updatedGas.value = sum;
    } else if (filledFactors.length === 4) {
      if (name !== "factor") {
        const sum =
          Number(gas.co2_factor) * AR5GasMultipliers.co2_factor +
          Number(gas.ch4_factor) * AR5GasMultipliers.ch4_factor +
          Number(gas.n2o_factor) * AR5GasMultipliers.n2o_factor;

        updatedGas.key = "factor";
        updatedGas.value = sum;
      } else {
        // Factor (CO2e) was changed, update CO2 factor
        let sum = 0;
        const allOtherFactors = factors.filter(
          (f) => f !== "co2_factor" && f !== "factor",
        );

        sum = Number(gas.factor);
        allOtherFactors.forEach((f) => {
          sum -= Number(gas[f]) * AR5GasMultipliers[f];
        });
        sum /= AR5GasMultipliers.co2_factor;

        updatedGas.key = "co2_factor";
        updatedGas.value = sum;
      }
    }

    if (updatedGas.key !== null && updatedGas.value !== null) {
      onFormUpdate(index, { name: updatedGas.key, value: updatedGas.value });
    }
  }

  function removeGas() {
    setIsEditable(true);
    onRemove(index);
    setIsEditable(false);
  }

  function handleUpdate(e) {
    const { name, value } = e.target;
    onFormUpdate(index, { name, value }, gas?.id || null);
  }

  const gasOptions = [
    {
      label: "CO₂e",
      value: "co2e",
    },
    ...GAS_OPTIONS,
  ];

  const actionMenuItems = [
    {
      label: "Delete",
      callback: () => removeGas(),
    },
  ];

  if (!commentVisible) {
    actionMenuItems.unshift({
      label: "Add comment",
      callback: () => {
        setCommentVisible(true);
      },
    });
  }

  const actionMenuRef = useRef(null);

  return (
    <div className={`relative overflow-visible ${className}`}>
      <div className="mb-4 flex w-full flex-row flex-wrap items-center justify-between border-l-4 border-borderColor pl-6">
        <LabeledSelect
          disabled={!isEditable}
          name="gas"
          value={gas.gas}
          containerClass="w-20 grow mr-4"
          title="Impact category*"
          options={gasOptions}
          defaultOption={"Select"}
          onChange={handleUpdate}
          required={true}
          error={errors?.[`gases.${index}.gas`]}
        />
        <LabeledInput
          disabled={!isEditable}
          name="factor"
          value={gas.factor}
          containerClass="w-20 grow"
          type="number"
          title={<AmountLabel />}
          placeholder="0"
          unit={`kg/${unit || "unit"}`}
          onChange={handleUpdate}
          onBlur={syncGasValues}
          required={true}
          error={errors?.[`gases.${index}.factor`]}
        />

        <LabeledSelect
          disabled={!isEditable}
          name="scope"
          value={gas.scope}
          containerClass="w-20 grow mx-4"
          title="Scope / LCA stage*"
          options={SCOPE_OPTIONS}
          onChange={handleUpdate}
          defaultOption={"Select scope"}
          required={true}
          error={errors?.[`gases.${index}.scope`]}
        />

        {isEditable && (
          <div className="flex flex-col justify-center">
            <button
              className="hover-circle relative z-10 ml-2 mt-6"
              onClick={(e) => {
                e.preventDefault();
                actionMenuRef.current.toggleMenu();
              }}
            >
              <MoreVertical size={24} className="shrink-0 cursor-pointer" />
              <ActionMenu ref={actionMenuRef} items={actionMenuItems} />
            </button>
          </div>
        )}

        <div className="mt-4 flex w-full">
          <div className="mt-6 mr-4 flex w-20 grow">
            <IconEnter className="scale-x-[-1] transform text-slate" />
            CH₄
          </div>

          <LabeledInput
            disabled={!isEditable}
            name="ch4_factor"
            value={gas?.ch4_factor || ""}
            containerClass="w-20 grow"
            type="number"
            title="Amount*"
            placeholder="0"
            unit={`kg/${unit || "unit"}`}
            onChange={handleUpdate}
            onBlur={syncGasValues}
            required={true}
            error={errors?.[`gases.${index}.ch4_factor`]}
          />

          <div className="mx-4 w-20 grow pl-2" />
          <div className="ml-2 h-8 w-8" />
        </div>

        <div className="mt-4 flex w-full">
          <div className="mt-6 mr-4 flex w-20 grow">
            <IconEnter className="scale-x-[-1] transform text-slate" />
            N₂O
          </div>

          <LabeledInput
            disabled={!isEditable}
            name="n2o_factor"
            value={gas?.n2o_factor || ""}
            containerClass="w-20 grow"
            type="number"
            title="Amount*"
            placeholder="0"
            unit={`kg/${unit || "unit"}`}
            onChange={handleUpdate}
            onBlur={syncGasValues}
            required={true}
            error={errors?.[`gases.${index}.n2o_factor`]}
          />

          <div className="mx-4 w-20 grow pl-2" />
          <div className="ml-2 h-8 w-8" />
        </div>

        <div className="mt-4 flex w-full">
          <div className="mt-6 mr-4 flex w-20 grow">
            <IconEnter className="scale-x-[-1] transform text-slate" />
            CO₂
          </div>

          <LabeledInput
            disabled={!isEditable}
            name="co2_factor"
            value={gas?.co2_factor || ""}
            containerClass="w-20 grow"
            type="number"
            title="Amount*"
            placeholder="0"
            unit={`kg/${unit || "unit"}`}
            onChange={handleUpdate}
            onBlur={syncGasValues}
            required={true}
            error={errors?.[`gases.${index}.co2_factor`]}
          />

          <div className="mx-4 w-20 grow pl-2" />
          <div className="ml-2 h-8 w-8" />
        </div>

        {commentVisible && (
          <FactorGasComment
            gas={gas}
            isDisabled={!isEditable}
            onFormUpdate={handleUpdate}
            setCommentVisible={setCommentVisible}
          />
        )}
      </div>

      {children}
    </div>
  );
}

function AmountLabel() {
  return (
    <div className="flex gap-2">
      Amount*
      <Info position="top">
        <p>
          We use AR5 values to calculate greenhouse gas emissions which is
          recommended by the greenhouse gas protocol. You can change to other
          formats from the results page.
        </p>
      </Info>
    </div>
  );
}
